import { useMemo } from 'react';
import SectionHeading from '@/pages/home/components/SectionHeading';
import { categories, videos } from '@/mocks/content';

interface HotVideosProps {
  category: string;
  onCategoryChange: (value: string) => void;
  keyword: string;
  onKeywordChange: (value: string) => void;
}

export default function HotVideos({ category, onCategoryChange, keyword, onKeywordChange }: HotVideosProps) {
  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return videos.filter((video) => {
      const matchCategory = category === '全部' || video.category === category;
      if (!matchCategory) return false;
      if (!kw) return true;
      const haystack = `${video.title} ${video.creator} ${video.category} ${video.desc}`.toLowerCase();
      return haystack.includes(kw);
    });
  }, [category, keyword]);

  const hasFilter = category !== '全部' || keyword.trim() !== '';

  return (
    <section id="hot" className="py-12 md:py-16 scroll-mt-20 bg-background-100/70">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between dv-reveal">
          <SectionHeading
            kicker="热门推荐"
            title="短视频热门推荐：按类型挑今天想看的那一个"
            desc="下面这些作品按综合热度整理，覆盖搞笑、生活、美食、游戏、音乐、影视与知识等方向。切换类型或在搜索框里输入关键词，列表会立即更新。"
          />

          <div className="w-full lg:w-auto">
            <label className="relative block w-full lg:w-72">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(event) => onKeywordChange(event.target.value)}
                placeholder="搜索标题、创作者或关键词"
                className="w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-9 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              {keyword ? (
                <button
                  type="button"
                  aria-label="清空搜索"
                  onClick={() => onKeywordChange('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 cursor-pointer"
                >
                  <i className="ri-close-circle-line" />
                </button>
              ) : null}
            </label>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2 dv-reveal">
          {categories.map((item) => {
            const active = item === category;
            return (
              <button
                key={item}
                type="button"
                onClick={() => onCategoryChange(item)}
                aria-pressed={active}
                className={`rounded-full px-3.5 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  active
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700'
                }`}
              >
                {item}
              </button>
            );
          })}
          {hasFilter ? (
            <button
              type="button"
              onClick={() => {
                onCategoryChange('全部');
                onKeywordChange('');
              }}
              className="inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-sm text-foreground-600 hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-refresh-line" />
              重置
            </button>
          ) : null}
        </div>

        <p className="mt-4 text-xs text-foreground-500">
          共找到 <span className="font-semibold text-foreground-800">{filtered.length}</span> 个作品
          {keyword.trim() ? (
            <span>
              ，关键词：<span className="text-primary-700">{keyword.trim()}</span>
            </span>
          ) : null}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((video) => (
              <article
                key={video.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative w-full h-[170px] md:h-[180px] overflow-hidden">
                  <img
                    src={video.cover}
                    alt={`${video.title} 视频封面`}
                    title={`${video.title} 短视频热门推荐`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/25" />
                  <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                    <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/90 text-primary-600 text-2xl">
                      <i className="ri-play-fill" />
                    </span>
                  </span>
                  <span className="absolute bottom-2 right-2 rounded bg-black/65 px-1.5 py-0.5 text-[11px] text-white">
                    {video.duration}
                  </span>
                  {video.badge ? (
                    <span className="absolute top-2 left-2 rounded bg-accent-500 px-2 py-0.5 text-[11px] font-medium text-foreground-950">
                      {video.badge}
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 line-clamp-2">
                    {video.title}
                  </h3>
                  <p className="mt-2 text-xs md:text-sm leading-relaxed text-foreground-600 line-clamp-2">
                    {video.desc}
                  </p>
                  <div className="mt-auto pt-3 flex items-center justify-between text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-800 text-[10px]">
                        <i className="ri-user-line" />
                      </span>
                      {video.creator}
                    </span>
                    <span className="inline-flex items-center gap-3">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-eye-line" /> {video.views}
                      </span>
                      <span className="inline-flex items-center gap-1 text-primary-700">
                        <i className="ri-heart-3-line" /> {video.likes}
                      </span>
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-5 rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center dv-reveal">
            <i className="ri-emotion-sad-line text-4xl text-foreground-400" />
            <p className="mt-3 text-sm text-foreground-700">没有找到匹配的作品</p>
            <p className="mt-1 text-xs text-foreground-500">换个关键词，或把类型切回「全部」试试</p>
            <button
              type="button"
              onClick={() => {
                onCategoryChange('全部');
                onKeywordChange('');
              }}
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line" /> 查看全部作品
            </button>
          </div>
        )}
      </div>
    </section>
  );
}